import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Movie } from '../../types/movie';
import styles from './HeroSlider.module.css';
import dramaData from '../../data/drama-full-1-short-dramas.json';
import DescriptionModal from '../common/DescriptionModal';

interface HeroSliderProps {
  items?: Movie[]; // 使items成为可选属性
}

const HeroSlider: React.FC<HeroSliderProps> = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  
  // 添加触摸滑动相关状态
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const minSwipeDistance = 50; // 最小滑动距离，单位为像素
  const [dragOffset, setDragOffset] = useState(0); // 拖动偏移量
  const [isDragging, setIsDragging] = useState(false); // 是否正在拖动

  const slideWidth = useRef<number>(0); // 幻灯片宽度，用于计算阻尼效果
  const sliderRef = useRef<HTMLDivElement>(null); // 滑块容器引用
  const [isButtonActive, setIsButtonActive] = useState<'prev' | 'next' | null>(null); // 按钮活跃状态
  const isMobile = useRef<boolean>(false); // 是否为移动设备
  const prevButtonRef = useRef<HTMLButtonElement>(null); // 上一张按钮引用
  const nextButtonRef = useRef<HTMLButtonElement>(null); // 下一张按钮引用
  
  // 使用drama-full-1-short-dramas.json中的前5条数据
  const sliderItems = dramaData.data.slice(0, 5) as Movie[];
  
  // 初始化和窗口大小改变时更新幻灯片宽度
  useEffect(() => {
    const updateSlideWidth = () => {
      if (sliderRef.current) {
        slideWidth.current = sliderRef.current.offsetWidth;
      }
    };
    
    // 检测是否为移动设备
    isMobile.current = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    
    updateSlideWidth();
    window.addEventListener('resize', updateSlideWidth);
    
    return () => {
      window.removeEventListener('resize', updateSlideWidth);
    };
  }, []);
  
  // 添加按钮焦点处理
  useEffect(() => {
    // 防止按钮保持焦点状态
    const handleButtonBlur = () => {
      if (prevButtonRef.current) {
        prevButtonRef.current.blur();
      }
      if (nextButtonRef.current) {
        nextButtonRef.current.blur();
      }
    };
    
    // 点击其他区域时移除按钮焦点
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.navButton}`)) {
        handleButtonBlur();
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);
  
  useEffect(() => {
    let interval: number;
    
    if (isAutoplay) {
      interval = window.setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % sliderItems.length);
      }, 5000);
    }
    
    return () => clearInterval(interval);
  }, [isAutoplay, sliderItems.length]);

  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
    setIsAutoplay(false);
    
    // 延时恢复自动播放
    setTimeout(() => {
      setIsAutoplay(true);
    }, 2000);
  };

  const handleMouseEnter = () => {
    setIsAutoplay(false);
  };

  const handleMouseLeave = () => {
    setIsAutoplay(true);
    setIsButtonActive(null); // 鼠标离开时重置按钮状态
  };

  const openModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedMovie(sliderItems[currentIndex]);
    setIsAutoplay(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsAutoplay(true);
  };

  // 切换到上一张幻灯片
  const goToPrevSlide = (e: React.MouseEvent) => {
    // 阻止默认行为，防止可能的焦点问题
    e.preventDefault();
    
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? sliderItems.length - 1 : prevIndex - 1
    );
    setIsAutoplay(false);
    setIsButtonActive('prev');
    
    // 提供触觉反馈（如果设备支持）
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    // 立即移除焦点
    if (prevButtonRef.current) {
      prevButtonRef.current.blur();
    }
    
    // 延时恢复自动播放和重置按钮状态
    setTimeout(() => {
      setIsAutoplay(true);
      setIsButtonActive(null);
    }, 2000);
  };

  // 切换到下一张幻灯片
  const goToNextSlide = (e: React.MouseEvent) => {
    // 阻止默认行为，防止可能的焦点问题
    e.preventDefault();
    
    setCurrentIndex((prevIndex) => (prevIndex + 1) % sliderItems.length);
    setIsAutoplay(false);
    setIsButtonActive('next');
    
    // 提供触觉反馈（如果设备支持）
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    // 立即移除焦点
    if (nextButtonRef.current) {
      nextButtonRef.current.blur();
    }
    
    // 延时恢复自动播放和重置按钮状态
    setTimeout(() => {
      setIsAutoplay(true);
      setIsButtonActive(null);
    }, 2000);
  };

  // 计算带阻尼效果的偏移量
  const calculateDampedOffset = (rawOffset: number): number => {
    // 如果幻灯片宽度未初始化，直接返回原始偏移量
    if (slideWidth.current === 0) {return rawOffset;}
    
    // 计算阻尼系数 (0.5 表示拖动距离只有实际移动的一半，产生阻尼感)
    const dampingFactor = 0.5;
    
    // 应用阻尼效果
    return rawOffset * dampingFactor;
  };

  // 触摸事件处理函数
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
    setDragOffset(0);
    setIsAutoplay(false);
    
    // 提供触觉反馈（如果设备支持）
    if (navigator.vibrate) {
      navigator.vibrate(10); // 轻微震动10毫秒
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) {return;}
    
    touchEndX.current = e.touches[0].clientX;
    const rawOffset = touchEndX.current - touchStartX.current;
    
    // 应用阻尼效果
    const dampedOffset = calculateDampedOffset(rawOffset);
    setDragOffset(dampedOffset);
    

  };

  const handleTouchEnd = () => {
    if (!isDragging) {return;}
    
    const distance = touchEndX.current - touchStartX.current;
    
    // 确保滑动距离足够长，避免误触
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // 向右滑动，显示上一张
        const event = { preventDefault: () => { /* 空实现 */ } } as React.MouseEvent;
        goToPrevSlide(event);
      } else {
        // 向左滑动，显示下一张
        const event = { preventDefault: () => { /* 空实现 */ } } as React.MouseEvent;
        goToNextSlide(event);
      }
    } else {
      // 如果滑动距离不够，添加弹回动画
      setDragOffset(0);
    }
    
    // 重置拖动状态
    setIsDragging(false);
    setDragOffset(0);
  };

  // 获取下一张和上一张幻灯片的索引
  const nextIndex = (currentIndex + 1) % sliderItems.length;
  const prevIndex = currentIndex === 0 ? sliderItems.length - 1 : currentIndex - 1;

  return (
    <>
      <div 
        ref={sliderRef}
        className={styles.heroSlider}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* 左右切换按钮 */}
        <button 
          ref={prevButtonRef}
          className={`${styles.navButton} ${styles.prevButton} ${isButtonActive === 'prev' ? styles.active : ''}`} 
          onClick={goToPrevSlide}
          aria-label="Previous slide"
          tabIndex={-1} // 防止按钮获得焦点
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <button 
          ref={nextButtonRef}
          className={`${styles.navButton} ${styles.nextButton} ${isButtonActive === 'next' ? styles.active : ''}`} 
          onClick={goToNextSlide}
          aria-label="Next slide"
          tabIndex={-1} // 防止按钮获得焦点
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <AnimatePresence mode="wait">
          {sliderItems.length > 0 && (
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: 1,
                x: isDragging ? dragOffset : 0 // 添加拖动效果
              }}
              exit={{ opacity: 0 }}
              transition={{ 
                duration: isDragging ? 0 : 0.5,
                type: isDragging ? 'spring' : 'tween',
                stiffness: 300,
                damping: 30
              }}
              className={styles.slide}
              style={{
                backgroundImage: `url(${sliderItems[currentIndex].coverImageW})`,
              }}
            >
              {/* 预加载相邻幻灯片的图片以提高性能 */}
              {!isDragging && (
                <>
                  <link rel="prefetch" href={sliderItems[nextIndex].coverImageW} />
                  <link rel="prefetch" href={sliderItems[prevIndex].coverImageW} />
                </>
              )}
              
              <div className={styles.slideContent}>
                <div className={styles.slideInfo}>
                  <motion.h2 
                    className={styles.slideTitle}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {sliderItems[currentIndex].title}
                  </motion.h2>
                  
                  <motion.div 
                    className={styles.slideMeta}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className={styles.slideYear}>{sliderItems[currentIndex].year}</span>
                    <span className={styles.slideDot}>•</span>
                    <span className={styles.slideEpisodes}>{sliderItems[currentIndex].episodes} Episodes</span>
                    <span className={styles.slideDot}>•</span>
                    <span className={styles.slideScore}>{sliderItems[currentIndex].score} Score</span>
                  </motion.div>
                  
                  <motion.p 
                    className={styles.slideDescription}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {sliderItems[currentIndex].description.substring(0, 150)}...
                  </motion.p>
                  
                  <motion.div 
                    className={styles.slideActions}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <a href={`details?id=${sliderItems[currentIndex]._id}&episode=1`} className={styles.watchButton}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
                      </svg>
                      Watch Now
                    </a>
                    
                    <button onClick={openModal} className={styles.detailsButton}>
                      More Details
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <div className={styles.indicators}>
          {sliderItems.map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
              onClick={() => handleIndicatorClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <DescriptionModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        movie={selectedMovie} 
      />
    </>
  );
};

export default HeroSlider; 